import { Box, Chip, Grid, Typography } from '@mui/material';
import { generatePath, Link } from 'react-router-dom';
import { ApplyProvider } from '../../containers/apply-provider/ApplyProvider';
import { APP_URLS } from '../../routes/urls';
import { moduleCardStyles } from '../../theme/styles/moduleCard';
import { fonts } from '../../theme/typography';
import { ProgramListItem } from '../../types/program';
import { PartialSome } from '../../types/util';
import { getDefaultImageSrc, loadDefaultImageOnError } from '../../utils/image';
import { formatToNepaliNumber, formatToNepaliPrice, getFormattedDate } from '../../utils/text';
import RequirementModal from '../requirement-modal/RequirementModal';
import { WishlistButton } from '../wishlist-button/WishlistButton';

interface ProgramCardProps {
  program: PartialSome<ProgramListItem, 'providers'>;
  instituteSlug: string;
  showRequirements?: boolean;
  hideProvider?: boolean;
  hideDeadlinePassed?: boolean;
}
export const ProgramCard: React.FC<ProgramCardProps> = ({
  program,
  instituteSlug,
  showRequirements,
  hideProvider = true,
  hideDeadlinePassed,
}) => {
  const programDetailUrl = generatePath(APP_URLS.singleProgram, {
    programSlug: program.slug,
    instituteSlug,
  });

  return (
    <ApplyProvider module="institute_programs" applyItems={program}>
      {({ renderApplyBtn, renderApplyDialog }) => (
        <>
          <Box sx={[moduleCardStyles.card, { p: 3 }]} component={Link} to={programDetailUrl}>
            {program.providers && !hideProvider && (
              <Box sx={{ mb: 3 }}>
                <Grid
                  container
                  spacing={1}
                  justifyContent="space-between"
                  direction={{ xs: 'column-reverse', md: 'row' }}
                >
                  <Grid item xs>
                    <Grid container alignItems="center" flexWrap="nowrap" spacing={1}>
                      <Grid item sx={moduleCardStyles.imgContainer}>
                        <img
                          src={program.providers.media || getDefaultImageSrc('institute')}
                          onError={loadDefaultImageOnError.bind(null, 'institute')}
                          alt={`Logo of ${program.providers.name}`}
                        />
                      </Grid>
                      <Grid item xs>
                        <Typography
                          variant="large"
                          component="h5"
                          sx={{ mb: 1 }}
                          fontFamily={fonts.heading}
                          fontWeight="Bold"
                        >
                          {program.providers.name}
                        </Typography>
                        <Typography variant="medium">{program.providers.address}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Box sx={{ textAlign: 'right' }}>
                      <WishlistButton moduleId={program.id} type="institute_programs" />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
            <Grid container spacing={2.5}>
              <Grid item xs>
                <Box>
                  <Typography
                    variant="large"
                    component="h6"
                    sx={{ mb: 1 }}
                    fontFamily={fonts.heading}
                    fontWeight="Bold"
                  >
                    {program.name}
                  </Typography>
                  <Grid container spacing={1} direction="column">
                    <Grid item>
                      <Grid container spacing={0.5}>
                        <Grid item>
                          <Typography variant="medium" color="secondary">
                            Deadline:
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography variant="medium" color="warning">
                            {getFormattedDate(program.deadline)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid container spacing={0.5}>
                        <Grid item>
                          <Typography variant="medium" color="secondary">
                            Cycle:
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography variant="medium" color="secondary">
                            {getFormattedDate(program.cycle)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              {(!program.providers || hideProvider) && (
                <Grid item>
                  <WishlistButton moduleId={program.id} type="institute_programs" />
                </Grid>
              )}
            </Grid>
            <Box sx={{ mt: 4 }}>
              <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                <Grid item>{renderApplyBtn({ hideDeadlinePassed })}</Grid>
                {/* <Grid item>
                  <Grid container spacing={0.5} alignItems="center" justifyContent="flex-end">
                    <Grid item>
                      <Typography color="secondary" variant="small" fontWeight="Medium">
                        Nrs.
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="large" fontWeight="Bold">
                        {formatToNepaliNumber(parseFloat(program.fees))}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid> */}
              </Grid>
            </Box>
            {showRequirements && program.eligibility_id && (
              <Box sx={{ mt: 1 }}>
                <RequirementModal
                  modules={[{ name: `${program.name}(${program.short_name})`, id: parseInt(program.id.toString()) }]}
                  module="institute_programs"
                />
              </Box>
            )}
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={moduleCardStyles.providerLabel} variant="medium" fontWeight="Medium">
                Affiliated to {program.affiliation.name}
              </Typography>
              {program.discount &&
                Boolean(program.discount.percentage && program.discount.amount) &&
                program.discount.percentage &&
                program.discount.amount && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      variant="light"
                      color="success"
                      sx={moduleCardStyles.discountBadge}
                      label={`Scholarship: ${program.discount.percentage}% ( ${formatToNepaliPrice(
                        program.discount.amount
                      )} )`}
                    />
                  </Box>
                )}
            </Box>
          </Box>
          {renderApplyDialog()}
        </>
      )}
    </ApplyProvider>
  );
};
